import { SearchProfile } from './data-contracts';
import { ContentType, HttpClient, RequestParams, TraceDataType } from './http-client';

import { loggerCan } from '@/data/utils/loggerUtil';
import { logger } from '@/logging/logger';

export class SearchProfileResource<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
	}

	/**
	 * No description
	 *
	 * @tags Search-Profile-Resource
	 * @name GetSearchProfile
	 * @summary Get the search profile.
	 * @request GET:/api/v2/documents/profiles/{profileName}
	 * @response `200` `(SearchProfile)[]` The request is completed successfully.
	 * @response `400` `(SearchProfile)[]` Bad request. Some of the inputs provided to the request are not valid.
	 * @response `404` `(SearchProfile)[]` The specified resource could not be found.
	 * @response `500` `(SearchProfile)[]` Internal server error. Additional details will be contained on the server logs.
	 */
	getSearchProfile = (profileName: string, params: RequestParams = {}) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['getSearchProfile'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/api/v2/documents/profiles/${profileName}`,
				params: _params,
				methodName: 'getSearchProfile',
			};
			logger.trace(traceData);
		}
		return this.http.request<SearchProfile[], SearchProfile[]>({
			path: `/api/v2/documents/profiles/${profileName}`,
			method: 'GET',
			format: params.format ?? 'json',
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @tags Search-Profile-Resource
	 * @name UpdateSearchProfile
	 * @summary Update the search profile.
	 * @request PUT:/api/v2/documents/profiles/{profileName}
	 * @response `200` `Record<string,string>` The request is completed successfully.
	 * @response `201` `Record<string,string>` The requested resource has been created.
	 * @response `400` `Record<string,string>` Bad request. Some of the inputs provided to the request are not valid.
	 * @response `500` `Record<string,string>` Internal server error. Additional details will be contained on the server logs.
	 */
	updateSearchProfile = (profileName: string, data: string, params: RequestParams = {}) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['updateSearchProfile'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/api/v2/documents/profiles/${profileName}`,
				params: _params,
				body: data,
				methodName: 'updateSearchProfile',
			};
			logger.trace(traceData);
		}
		return this.http.request<Record<string, string>, Record<string, string>>({
			path: `/api/v2/documents/profiles/${profileName}`,
			method: 'PUT',
			body: data,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @tags Search-Profile-Resource
	 * @name CreateSearchProfile
	 * @summary Create the search profile.
	 * @request POST:/api/v2/documents/profiles/{profileName}
	 * @response `201` `Record<string,string>` The requested resource has been created.
	 * @response `400` `Record<string,string>` Bad request. Some of the inputs provided to the request are not valid.
	 * @response `409` `Record<string,string>` The specified resource already exist.
	 * @response `500` `Record<string,string>` Internal server error. Additional details will be contained on the server logs.
	 */
	createSearchProfile = (profileName: string, data: string, params: RequestParams = {}) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['createSearchProfile'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/api/v2/documents/profiles/${profileName}`,
				params: _params,
				body: data,
				methodName: 'createSearchProfile',
			};
			logger.trace(traceData);
		}
		return this.http.request<Record<string, string>, Record<string, string>>({
			path: `/api/v2/documents/profiles/${profileName}`,
			method: 'POST',
			body: data,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @tags Search-Profile-Resource
	 * @name DeleteProfile
	 * @summary Delete the search profile.
	 * @request DELETE:/api/v2/documents/profiles/{profileName}
	 * @response `200` `Record<string,string>` The request is completed successfully.
	 * @response `204` `Record<string,string>` The requested completed successfully. No content is returned in the response.
	 * @response `400` `Record<string,string>` Bad request. Some of the inputs provided to the request are not valid.
	 * @response `500` `Record<string,string>` Internal server error. Additional details will be contained on the server logs.
	 */
	deleteProfile = (profileName: string, params: RequestParams = {}) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['deleteProfile'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/api/v2/documents/profiles/${profileName}`,
				params: _params,
				methodName: 'deleteProfile',
			};
			logger.trace(traceData);
		}
		return this.http.request<Record<string, string>, Record<string, string>>({
			path: `/api/v2/documents/profiles/${profileName}`,
			method: 'DELETE',
			format: params.format ?? 'json',
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * No description
	 *
	 * @tags Search-Profile-Resource
	 * @name GetSearchProfileList
	 * @summary Gets the search profiles.
	 * @request GET:/api/v2/documents/profiles
	 * @response `200` `(SearchProfile)[]` The request is completed successfully.
	 * @response `400` `(SearchProfile)[]` Bad request. Some of the inputs provided to the request are not valid.
	 * @response `404` `(SearchProfile)[]` The specified resource could not be found.
	 * @response `500` `(SearchProfile)[]` Internal server error. Additional details will be contained on the server logs.
	 */
	getSearchProfileList = (params: RequestParams = {}) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['getSearchProfileList'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/api/v2/documents/profiles`,
				params: _params,
				methodName: 'getSearchProfileList',
			};
			logger.trace(traceData);
		}
		return this.http.request<SearchProfile[], SearchProfile[]>({
			path: `/api/v2/documents/profiles`,
			method: 'GET',
			format: params.format ?? 'json',
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
}
